
import React, { useEffect } from 'react';
import { NewDashboardHeader } from '../../components/NewDashboardHeader';
import { Footer } from '../../components/Footer';
import { useReferralWizard } from '../../hooks/useReferralWizard';
import { PropertySearchStep } from '../../components/public/referral/PropertySearchStep';
import { BrokerFormStep } from '../../components/public/referral/BrokerFormStep';
import { ClientFormStep } from '../../components/public/referral/ClientFormStep';
import { CIEN_CUADRAS_BUTTON_BLUE, CIEN_CUADRAS_BLUE } from '../../firebase/firebaseConfig';
import { SpinnerIcon, ChevronLeftIcon, CheckCircleIcon, ExclamationTriangleIcon, MortgageIcon } from '../../components/Icons';

export const PublicReferralView: React.FC = () => {
  const wizard = useReferralWizard();

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1]);
    const code = params.get('code');
    if (code) {
      wizard.setSearchCode(code);
      wizard.setIsCiencuadrasProperty(true);
      if (params.get('lock') === 'true') wizard.setIsLocked(true);
      wizard.performSearch(code);
    }
  }, [wizard.performSearch]);

  const handleNextToStep3 = async () => {
    await wizard.handleLogBrokerData();
    wizard.setStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const iframeUrl = "https://tools.zonacompradores.com/referirrapido/co/?referralCode=LML3ExB6H1hZ";

  const isFormValid = wizard.step === 2 
    ? (wizard.formData.nombre_broker && wizard.formData.email_broker && wizard.formData.telefono_broker)
    : (wizard.formData.nombre_cliente && wizard.formData.email_cliente && wizard.formData.ingresos_cliente);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <NewDashboardHeader isNewlyAddedMode={false} />
      
      <main className="flex-grow flex flex-col items-center py-10 px-4">
        {/* Header Decorativo */}
        <div className="w-full max-w-4xl text-center mb-10">
          <div className="inline-flex p-3 bg-sky-100 rounded-2xl mb-4">
             <MortgageIcon className="w-8 h-8 text-sky-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-[#00234F] uppercase tracking-tighter mb-2">Referido de Crédito</h1>
          <p className="text-gray-500 font-medium">Gana una comisión por cada cliente que desembolse su crédito con nosotros.</p>
        </div>

        <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col min-h-[600px] border border-gray-100">
          {/* Progress Bar Superior */}
          <div className="bg-gray-50/50 px-8 py-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex flex-col">
              <span className="text-[10px] font-black text-sky-600 uppercase tracking-[0.2em] mb-1">Paso Actual</span>
              <h2 className="text-lg font-black text-gray-800 uppercase tracking-tight">
                {wizard.step === 1 ? 'Identificación del Inmueble' : wizard.step === 2 ? 'Información del Broker' : 'Información del Cliente'}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-xs transition-all border-2 ${wizard.step === i ? 'bg-[#00234F] text-white border-[#00234F] scale-110 shadow-lg' : wizard.step > i ? 'bg-green-500 text-white border-green-500' : 'bg-white text-gray-300 border-gray-200'}`}>
                    {wizard.step > i ? <CheckCircleIcon className="w-5 h-5"/> : `0${i}`}
                  </div>
                  {i < 3 && <div className={`w-6 h-0.5 ${wizard.step > i ? 'bg-green-500' : 'bg-gray-100'}`}></div>}
                </div>
              ))}
            </div>
          </div>

          <div className="flex-grow">
            {wizard.step === 1 && <PropertySearchStep state={wizard} actions={wizard} />}
            {wizard.step === 2 && <BrokerFormStep formData={wizard.formData} onFormChange={(e: any) => wizard.setFormData({...wizard.formData, [e.target.name]: e.target.value})} errors={{}} />}
            {wizard.step === 3 && (
              wizard.useInternalForm ? (
                wizard.submissionStatus === 'success' ? (
                  <div className="p-20 text-center space-y-6 animate-fade-in">
                    <div className="w-24 h-24 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircleIcon className="w-16 h-16" />
                    </div>
                    <h3 className="text-3xl font-black text-gray-800 uppercase tracking-tighter">¡Referido Registrado!</h3>
                    <p className="text-gray-500 max-w-sm mx-auto leading-relaxed">Hemos recibido los datos del cliente. Un especialista de <span className="font-bold text-sky-600">{wizard.selectedBank}</span> se pondrá en contacto pronto para iniciar la gestión.</p>
                    <button onClick={() => window.location.hash = ''} className="px-10 py-3 bg-[#00234F] text-white rounded-xl font-black uppercase text-xs tracking-widest shadow-xl hover:bg-black transition-all active:scale-95 mt-4">Regresar al Home</button>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <ClientFormStep formData={wizard.formData} onFormChange={(e: any) => wizard.setFormData({...wizard.formData, [e.target.name]: e.target.value})} errors={{}} />
                    {wizard.submissionStatus === 'error' && (
                        <div className="mx-8 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 animate-shake">
                            <ExclamationTriangleIcon className="w-5 h-5 text-red-500"/>
                            <p className="text-xs text-red-700 font-bold uppercase tracking-tight">{wizard.errorMessage || "Error al enviar el referido. Por favor intenta de nuevo."}</p>
                        </div>
                    )}
                  </div>
                )
              ) : (
                <div className="h-[600px] flex flex-col">
                  <div className="p-6 bg-gray-50/50 border-b flex justify-between items-center">
                    <button onClick={() => wizard.setStep(2)} className="text-[10px] font-black text-sky-600 uppercase tracking-widest flex items-center hover:bg-sky-100 px-4 py-2 rounded-xl transition-all border border-sky-100 bg-white">
                        <ChevronLeftIcon className="w-4 h-4 mr-1"/> Volver
                    </button>
                    <div className="text-right">
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Entidad Seleccionada</p>
                        <h3 className="font-black text-gray-800 uppercase tracking-tight">{wizard.selectedBank}</h3>
                    </div>
                  </div>
                  <iframe src={iframeUrl} className="w-full flex-grow border-0" allow="camera; microphone; geolocation" />
                </div>
              )
            )}
          </div>

          {wizard.step < 3 && (
            <footer className="bg-gray-50/50 px-8 py-6 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-[10px] font-bold text-gray-400 uppercase max-w-xs leading-tight">
                * Campos obligatorios para procesar el registro y asegurar tu comisión.
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                {wizard.step === 2 && (
                    <button 
                        onClick={() => wizard.setStep(1)} 
                        className="flex-1 sm:flex-none px-8 py-3 rounded-xl border-2 border-gray-200 bg-white text-xs font-black uppercase tracking-widest text-gray-400 hover:border-sky-500 hover:text-sky-600 transition-all"
                    >Atrás</button>
                )}
                <button 
                    onClick={wizard.step === 1 ? () => wizard.setStep(2) : handleNextToStep3}
                    disabled={wizard.step === 1 && ((wizard.isCiencuadrasProperty && !wizard.selectedProperty) || !wizard.selectedBank) || (wizard.step === 2 && !isFormValid)}
                    className="flex-1 sm:flex-none px-12 py-3 rounded-xl text-white font-black text-xs uppercase tracking-[0.2em] shadow-2xl disabled:opacity-30 transition-all active:scale-95"
                    style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                >Siguiente Paso</button>
              </div>
            </footer>
          )}

          {wizard.step === 3 && wizard.useInternalForm && wizard.submissionStatus !== 'success' && (
            <footer className="bg-gray-50/50 px-8 py-6 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-[10px] font-bold text-gray-400 uppercase max-w-xs leading-tight">
                    Al confirmar, el cliente recibirá una notificación de contacto por parte de {wizard.selectedBank}.
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <button onClick={() => wizard.setStep(2)} disabled={wizard.submissionStatus === 'loading'} className="flex-1 sm:flex-none px-8 py-3 rounded-xl border-2 border-gray-200 bg-white text-xs font-black uppercase tracking-widest text-gray-400 hover:border-sky-500 hover:text-sky-600 transition-all">Atrás</button>
                  <button 
                    onClick={wizard.submitInternalForm}
                    disabled={!isFormValid || wizard.submissionStatus === 'loading'}
                    className="flex-1 sm:flex-none px-12 py-3 rounded-xl text-white font-black text-xs uppercase tracking-[0.2em] shadow-2xl disabled:opacity-30 transition-all active:scale-95 flex items-center justify-center gap-3"
                    style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                  >
                    {wizard.submissionStatus === 'loading' ? <SpinnerIcon className="w-5 h-5 animate-spin"/> : null}
                    {wizard.submissionStatus === 'loading' ? 'Enviando...' : 'Finalizar Registro'}
                  </button>
                </div>
            </footer>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};
